import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AvikaLogo } from '../components/AvikaLogo';

// Eye toggle icon
const EyeIcon = ({ open }: { open: boolean }) =>
  open ? (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943
           9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ) : (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7
           a9.97 9.97 0 012.09-3.342M9.88 9.88a3 3 0 104.243 4.243M3 3l18 18" />
    </svg>
  );

// Decorative geometric lines for left panel
const PanelDecor = () => (
  <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.06]"
    viewBox="0 0 480 800" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    <line x1="0" y1="0"   x2="480" y2="800" stroke="#C4956A" strokeWidth="0.6" />
    <line x1="480" y1="0" x2="0"   y2="800" stroke="#C4956A" strokeWidth="0.6" />
    {[100, 180, 260, 340, 420].map((r, i) => (
      <circle key={i} cx="240" cy="400" r={r} stroke="#C4956A" strokeWidth="0.5" fill="none" />
    ))}
  </svg>
);

// Animated quote carousel
const quotes = [
  { text: 'Every voice matters. Every workplace must be safe.', attr: 'POSH Act, 2013' },
  { text: 'Prevention is the highest form of compliance.', attr: 'Avika Platform' },
  { text: "Safe spaces don't happen by accident. They are built, intentionally.", attr: 'Avika Platform' },
];

const QuoteCarousel: React.FC = () => {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % quotes.length), 5000);
    return () => clearInterval(t);
  }, []);
  const q = quotes[idx];
  return (
    <div className="text-center max-w-xs">
      <p className="font-display italic text-lg font-light leading-relaxed mb-3 transition-all duration-700"
        style={{ color: '#8A6030' }}>
        &ldquo;{q.text}&rdquo;
      </p>
      <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: '#A07040' }}>
        &mdash; {q.attr}
      </p>
      <div className="flex justify-center gap-1.5 mt-5">
        {quotes.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)}
            className="rounded-full transition-all duration-300"
            style={{
              width: i === idx ? 20 : 6, height: 6,
              background: i === idx ? '#C4956A' : 'rgba(160,112,64,0.35)',
            }} />
        ))}
      </div>
    </div>
  );
};

// Gold orb glows
const Orbs = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    <div className="absolute w-80 h-80 rounded-full -top-20 -right-20 opacity-10"
      style={{ background: 'radial-gradient(circle, #C4956A 0%, transparent 70%)', filter: 'blur(40px)' }} />
    <div className="absolute w-60 h-60 rounded-full bottom-10 -left-10 opacity-[0.08]"
      style={{ background: 'radial-gradient(circle, #C4956A 0%, transparent 70%)', filter: 'blur(50px)' }} />
  </div>
);

// Trust badge icons (SVG, no emoji)
const LockIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#C4956A" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);
const BuildingIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#C4956A" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);
const ShieldIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#C4956A" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

// Input field
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  icon: React.ReactNode;
  suffix?: React.ReactNode;
  error?: boolean;
}
const Field: React.FC<InputProps> = ({ label, icon, suffix, error, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-widest"
        style={{ color: '#9E9A95' }}>
        {label}
      </label>
      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: '#6E6A65' }}>
          {icon}
        </span>
        <input
          {...props}
          onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
          onBlur={(e)  => { setFocused(false); props.onBlur?.(e); }}
          className="w-full pl-10 pr-10 py-3 text-sm outline-none transition-all duration-200 rounded-xl"
          style={{
            background: focused ? '#FAFAFA' : '#F5F3F0',
            border: `1.5px solid ${error ? '#ef4444' : focused ? '#C4956A' : '#E5E1DC'}`,
            color: '#141414',
            boxShadow: focused ? '0 0 0 3px rgba(196,149,106,0.12)' : 'none',
          }}
        />
        {suffix && (
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2" style={{ color: '#9E9A95' }}>
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
};

// Main Login Page
const LoginPage: React.FC = () => {
  const { user, login, completeMFA } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);
  
  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [mfaCompleted, setMfaCompleted] = useState(false);

  // Navigate to dashboard after user state is set from context
  useEffect(() => {
    if (mfaCompleted && user) {
      navigate('/app/dashboard', { replace: true });
    }
  }, [mfaCompleted, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await login(email, password);
      
      // Check if MFA challenge is required
      if (result && 'mfa_required' in result && result.mfa_required) {
        setMfaRequired(true);
        setMfaToken(result.mfa_token);
        setLoading(false);
        return;
      }
      
      navigate('/app/dashboard');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Incorrect credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMFASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (totpCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await completeMFA(mfaToken, totpCode);
      // Set flag to trigger navigation in useEffect after context updates
      setMfaCompleted(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Invalid authentication code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* LEFT PANEL - dark brand */}
      <div className="hidden lg:flex flex-col relative w-[52%] overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #FAF8F5 0%, #F5F1EC 60%, #F8F4EE 100%)' }}>

        <PanelDecor />
        <Orbs />

        {/* Back to home */}
        <div className="relative z-10 p-10">
          <Link to="/"
            className="inline-flex items-center gap-2 text-xs font-medium transition-colors hover:opacity-80"
            style={{ color: '#9E7A55' }}>
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to home
          </Link>
        </div>

        {/* Centre content */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-12 pb-16 gap-12">
          <div className="animate-float">
            <AvikaLogo height={100} variant="dark" />
          </div>

          <QuoteCarousel />

          {/* Trust badges */}
          <div className="flex items-center gap-8">
            {[
              { icon: <LockIcon />, label: 'Secure' },
              { icon: <BuildingIcon />, label: 'Govt. aligned' },
              { icon: <ShieldIcon />, label: 'Confidential' },
            ].map((b) => (
              <div key={b.label} className="flex flex-col items-center gap-1.5">
                {b.icon}
                <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#777777' }}>
                  {b.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom gold rule */}
        <div className="relative z-10 h-0.5"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(196,149,106,0.5) 20%, rgba(196,149,106,0.8) 50%, rgba(196,149,106,0.5) 80%, transparent)' }} />
      </div>

      {/* RIGHT PANEL - white form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12"
        style={{ background: '#FDFCFB' }}>

        {/* Mobile logo */}
        <div className="lg:hidden mb-10">
          <AvikaLogo height={72} variant="dark" />
        </div>

        <div className="w-full max-w-sm animate-fade-up">

          {/* Heading */}
          <div className="mb-8">
            <h1 className="font-display text-3xl font-semibold mb-2" style={{ color: '#141414' }}>
              {mfaRequired ? 'Two-Factor Authentication' : 'Welcome back'}
            </h1>
            <p className="text-sm" style={{ color: '#9E9A95' }}>
              {mfaRequired 
                ? 'Enter the code from your authenticator app' 
                : 'Sign in to your compliance workspace'}
            </p>
          </div>

          {/* Error banner */}
          {error && (
            <div className="mb-5 flex items-start gap-2.5 p-3.5 rounded-xl text-sm animate-fade-up"
              style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' }}>
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          {/* MFA Challenge Form */}
          {mfaRequired ? (
            <form onSubmit={handleMFASubmit} className="space-y-5">
              <Field
                label="Authentication Code"
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                placeholder="000000"
                maxLength={6}
                autoComplete="one-time-code"
                error={!!error}
                icon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                }
              />

              <button
                type="submit"
                disabled={loading || totpCode.length !== 6}
                className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200
                           hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed
                           flex items-center justify-center gap-2.5"
                style={{
                  background: loading
                    ? '#D4B896'
                    : 'linear-gradient(135deg, #C4956A 0%, #E8C4A0 50%, #C4956A 100%)',
                  backgroundSize: '200% auto',
                  color: '#141414',
                  boxShadow: loading ? 'none' : '0 4px 20px rgba(196,149,106,0.35)',
                }}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify Code
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setMfaRequired(false);
                  setMfaToken('');
                  setTotpCode('');
                  setError(null);
                }}
                className="w-full py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                ← Back to login
              </button>
            </form>
          ) : (
            /* Normal Login Form */
            <form onSubmit={handleSubmit} className="space-y-5">
              <Field
                label="Email address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@organisation.com"
                error={!!error}
                icon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                }
              />

              <Field
                label="Password"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="Enter your password"
                error={!!error}
                icon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                }
                suffix={
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    className="transition-colors hover:opacity-70"
                    aria-label={showPw ? 'Hide password' : 'Show password'}>
                    <EyeIcon open={showPw} />
                  </button>
                }
              />

              {/* Forgot password link */}
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium transition-colors hover:underline"
                  style={{ color: '#C4956A' }}
                >
                  Forgot password?
                </Link>
              </div>

              {/* Sign in button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200
                           hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed
                           flex items-center justify-center gap-2.5"
                style={{
                background: loading
                  ? '#D4B896'
                  : 'linear-gradient(135deg, #C4956A 0%, #E8C4A0 50%, #C4956A 100%)',
                backgroundSize: '200% auto',
                color: '#141414',
                boxShadow: loading ? 'none' : '0 4px 20px rgba(196,149,106,0.35)',
              }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </form>
          )}

          {/* Divider */}
          <div className="flex items-center gap-3 my-7">
            <div className="flex-1 h-px" style={{ background: '#E5E1DC' }} />
            <span className="text-xs font-medium" style={{ color: '#C0BAB3' }}>POSH Act, 2013</span>
            <div className="flex-1 h-px" style={{ background: '#E5E1DC' }} />
          </div>

          {/* Compliance note */}
          <p className="text-center text-xs leading-relaxed" style={{ color: '#B0AAA3' }}>
            Avika is the compliance workspace for the{' '}
            <span style={{ color: '#9E9A95' }}>
              Prevention of Sexual Harassment Act, 2013.
            </span>{' '}
            All data is secured and access-controlled.
          </p>

          {/* Back to home - mobile */}
          <div className="mt-8 text-center lg:hidden">
            <Link to="/" className="text-xs font-medium transition-colors hover:underline"
              style={{ color: '#C4956A' }}>
              &larr; Back to home
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-12 text-xs" style={{ color: '#D0CABE' }}>
          &copy; {new Date().getFullYear()} Avika &middot; Secure, confidential, compliant
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
